import React, { PureComponent } from 'react';
import { layer, source, feature, interaction, MapContext } from 'react-openlayers';
import Select from 'ol/interaction/Select';
import { GeoJSON } from 'ol/format';
import { SCHEDULE_TYPES } from '../helpers/scheduleTypes';
import { generateColor } from '../../../utils';
import { BLACK, WHITE, PLUM } from '../../../rgbColors';
import GeometryType from 'ol/geom/GeometryType';
import Collection from 'ol/Collection';
import { polygon as turfPolygon } from '@turf/helpers';
import Feature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import booleanContains from '@turf/boolean-contains';
import BaseEvent from 'ol/events/Event';
import { eventIsDrawEvent } from '../../../types/types';
import { Geometry, Polygon } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';
import { MapZoomStorage } from '../../../db/local_storage';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';

import { Style, Fill, Stroke, Text, Circle as CircleStyle } from 'ol/style';

interface ExpandedFeature {
  originalFeature: Feature<Geometry>;
  expandedCoord: Coordinate;
}

interface SchedulingLayerProps {
  selectedFeatures: Collection<Feature>;
  scheduleFeatures: Feature[];
  updateSelectedFeatures: (features: Collection<Feature>) => void;
  statsDrawActive: boolean;
  displayStats: (fields: Feature<Geometry>[]) => Promise<void>;
}

interface SchedulingLayerState {
  expandedFeatures: ExpandedFeature[];
  expandedCenter: any;
}

export default class SchedulingLayer extends PureComponent<SchedulingLayerProps, SchedulingLayerState> {
  static contextType = MapContext;
  declare context: React.ContextType<typeof MapContext>;

  FIELD_TRANSPARENCY = '40'; // 25% opacity

  HIDE_TEXT_ZOOM = 12;

  VALID_TYPES = [SCHEDULE_TYPES.PULLIN, SCHEDULE_TYPES.ORDER_LINE, SCHEDULE_TYPES.FIELD];
  selectable: boolean;

  private vectorLayer: VectorLayer;

  private selectInteraction: Select;

  // See MapInteractionsLayer.waitForMap for the full rationale. Any
  // unmount with a pending retry leaks the callback onto the old
  // instance, which then attaches a SECOND click listener, Select
  // interaction, and VectorLayer to the live map.
  private waitForMapTimer: NodeJS.Timeout | undefined;

  constructor(props: SchedulingLayerProps) {
    super(props);

    this.filterSelection = this.filterSelection.bind(this);
    this.updateFeatures = this.updateFeatures.bind(this);

    this.selectable = true;

    this.state = {
      expandedFeatures: [],
      expandedCenter: null,
    };

    this.styleFunction = this.styleFunction.bind(this);
  }

  componentDidMount(): void {
    this.waitForMap();
  }

  componentWillUnmount() {
    clearTimeout(this.waitForMapTimer);
    this.waitForMapTimer = undefined;

    const map = this.context?.map;

    if (map) {
      if (this.selectInteraction) {
        // @ts-ignore - react-openlayers type mismatch
        map.removeInteraction(this.selectInteraction);
      }

      map.un('click', this.handleMapClick);
      // @ts-expect-error - react-openlayers type mismatch
      map.removeLayer(this.vectorLayer);
    }
  }

  componentDidUpdate(prevProps: SchedulingLayerProps) {
    if (
      prevProps.scheduleFeatures !== this.props.scheduleFeatures ||
      prevProps.selectedFeatures !== this.props.selectedFeatures
    ) {
      this.updateFeatures(this.props.scheduleFeatures);
    }
  }

  updateFeatures(features: any[]) {
    if (!this.context?.map || !this.vectorLayer) {
      return;
    }

    console.time('SchedulingLayer: updateFeatures');

    const source = this.vectorLayer.getSource();
    source.clear();

    // no featureProjection, because your data is already in 3857
    const olFeatures = features.map((f) => new GeoJSON().readFeature(f));

    // preserve extra props for styling
    olFeatures.forEach((olFeature, i) => {
      const props = olFeature.getProperties();
      const selected = this.props.selectedFeatures.getArray().some((sf) => sf.getProperties().job_id === props.job_id);

      olFeature.set('selected', selected);
      olFeature.set('selectedIdx', i);

      const type = props.type;

      if (type === SCHEDULE_TYPES.PULLIN) {
        olFeature.setId(`${props.field_name}-${props.job_id}-Pullin`);
      }
    });

    source.addFeatures(olFeatures);

    console.timeEnd('SchedulingLayer: updateFeatures');
  }

  waitForMap = () => {
    if (this.context?.map) {
      this.context.map.on('click', this.handleMapClick);

      this.vectorLayer = new VectorLayer({
        source: new VectorSource(),
        zIndex: 1,
        style: this.styleFunction,
      });
      // @ts-expect-error - react-openlayers type mismatch
      this.context.map.addLayer(this.vectorLayer);

      // Add OL Select interaction directly
      this.selectInteraction = new Select({
        layers: [this.vectorLayer], // <-- only select features on this layer
        multi: false,
        filter: this.filterSelection,
        style: null, // keep style unchanged on selection if needed
      });

      // @ts-ignore
      this.context.map.addInteraction(this.selectInteraction);
    } else {
      this.waitForMapTimer = setTimeout(this.waitForMap, 100); // try again in 100ms
    }
  };

  handleMapClick = (event: any) => {
    // Skip if draw interaction is active
    if (this.props.statsDrawActive) {
      return;
    }

    const map = this.context?.map;

    if (!map) {
      return;
    }

    const features = map
      .getFeaturesAtPixel(event.pixel)
      .filter((feature) => feature.getProperties().type === SCHEDULE_TYPES.PULLIN);

    if (!features || features.length === 0) {
      // background click
      this.setState({ expandedFeatures: [], expandedCenter: null });

      return;
    }

    const feature = features[0];
    const featureId = feature.getId();
    const isExpandedFeature = this.state.expandedFeatures.some(
      ({ originalFeature }) => originalFeature.getId?.() + '-expanded' === featureId,
    );

    if (isExpandedFeature) {
      return;
    }

    if (features.length === 1) {
      // not overlapping — treat as normal selection
      this.setState({ expandedFeatures: [], expandedCenter: null });

      return;
    }

    // Overlapping → expand
    // @ts-ignore
    const centerCoord = features[0].getGeometry()?.getCoordinates(); // already in EPSG:3857
    // A more general approach - good for any number of features
    // However, we don't need it in our case, since we only
    // are going to have 2 or 3 features.
    // const angleStep = (2 * Math.PI) / features.length;
    const angleStep = (30 * Math.PI) / 180; // 30 degrees per step
    const startAngle = angleStep; // starts at 30°

    const zoomLevel = MapZoomStorage.get().value;
    // We want about 65 meters at zoom level 19
    const radius = 65 * 2 ** (19 - zoomLevel);

    // @ts-ignore
    const expandedFeatures: ExpandedFeature[] = features.map((feature, i) => {
      const angle = startAngle + i * angleStep;
      const dx = Math.cos(angle) * radius;
      const dy = Math.sin(angle) * radius;

      return {
        originalFeature: feature,
        expandedCoord: [centerCoord[0] + dx, centerCoord[1] + dy],
      };
    });

    this.setState({ expandedFeatures, expandedCenter: centerCoord });
  };

  /**
   * Workaround to add and remove multiple features that aren't necessarily selected, but are related
   */
  filterSelection(feature: FeatureLike) {
    if (this.selectable) {
      this.selectable = false;

      const selectedFeatures = this.props.selectedFeatures.getArray().slice();
      const pullinSelectedIdx = selectedFeatures.findIndex(
        (selectedFeature) =>
          selectedFeature.getProperties().type === SCHEDULE_TYPES.PULLIN &&
          selectedFeature.getProperties().field_name === feature.getProperties().field_name,
      );
      const fieldSelectedIdx = selectedFeatures.findIndex(
        (selectedFeature) =>
          selectedFeature.getProperties().type === SCHEDULE_TYPES.FIELD &&
          selectedFeature.getProperties().field_name === feature.getProperties().field_name,
      );

      if (pullinSelectedIdx !== -1 || fieldSelectedIdx !== -1) {
        if (pullinSelectedIdx !== -1) {
          selectedFeatures.splice(pullinSelectedIdx, 1);
        }

        if (fieldSelectedIdx !== -1) {
          selectedFeatures.splice(fieldSelectedIdx, 1);
        }
      } else {
        if (this.VALID_TYPES.includes(feature.getProperties().type)) {
          selectedFeatures.push(feature as Feature<Geometry>);
        }
      }

      const newSelected = new Collection(selectedFeatures);

      this.props.updateSelectedFeatures(newSelected);

      // prevents multiple features getting selected at once
      setTimeout(() => (this.selectable = true), 500);
    }

    return false;
  }

  showStatsForFields = async (event: BaseEvent) => {
    if (!eventIsDrawEvent(event)) {
      return;
    }

    const coordinates = (event.feature.getGeometry() as Polygon).getCoordinates();
    const statsPolygon = turfPolygon(coordinates);
    // TODO temporay cheeat
    const fieldsInPolygon = this.props.scheduleFeatures.filter((scheduleFeature: any) => {
      if (scheduleFeature.geometry.type === GeometryType.POLYGON) {
        const fieldPolygon = turfPolygon(
          scheduleFeature.geometry.coordinates.filter((coords: Coordinate[]) => coords.length > 3),
        );

        return booleanContains(statsPolygon, fieldPolygon);
      } else if (scheduleFeature.geometry.type === GeometryType.MULTI_POLYGON) {
        for (const polygon of scheduleFeature.geometry.coordinates.filter(
          (coords: Coordinate[]) => coords.length > 3,
        )) {
          const fieldPolygon = turfPolygon(polygon);

          if (booleanContains(statsPolygon, fieldPolygon)) {
            return true;
          }
        }
      }

      return false;
    });
    await this.props.displayStats(fieldsInPolygon);
  };

  styleFunction(feature: any) {
    const map = this.context?.map;

    if (!map) {
      return new Style({});
    }

    const props = feature.getProperties();
    const type = props.type;

    switch (type) {
      case SCHEDULE_TYPES.FIELD: {
        const fieldColor = props.fillColor || generateColor(props.robot || '');
        const selected = Boolean(
          feature.get('selected'), // mark this manually when you select
        );

        return new Style({
          stroke: new Stroke({
            color: BLACK,
            width: selected ? 2 : 1,
          }),
          fill: new Fill({
            color: `${fieldColor}80`, // add transparency (hex + alpha)
          }),
          text: new Text({
            text: props.field_name,
            font: '25px Calibri,sans-serif',
            fill: new Fill({ color: BLACK }),
          }),
        });
      }

      case SCHEDULE_TYPES.ORDER_LINE: {
        const lineColor = generateColor(props.robot);

        return new Style({
          stroke: new Stroke({
            color: lineColor,
            width: 2,
          }),
        });
      }

      case SCHEDULE_TYPES.PULLIN: {
        const isDropoff = props.pullinType === 'Dropoffs';
        const fillColor = isDropoff
          ? generateColor(props.operatorName || props.pullinType)
          : generateColor(props.robot || '');

        const selected = props.selected;
        const selectedIdx = props.selectedIdx ?? -1;
        const selectedText = selectedIdx !== -1 ? ` / ${selectedIdx}` : '';

        return new Style({
          image: new CircleStyle({
            radius: (selected ? 8 : 6) + (props.first ? 2 : 0),
            fill: new Fill({ color: fillColor }),
            stroke: new Stroke({ color: WHITE, width: 2 }),
          }),
          text: new Text({
            text:
              map.getView().getZoom()! >= this.HIDE_TEXT_ZOOM
                ? (props.field_name || props.dropoff_name) + selectedText
                : '',
            font: '20px Calibri,sans-serif',
            fill: new Fill({ color: WHITE }),
            stroke: new Stroke({ color: BLACK, width: 2 }),
            offsetY: 20,
          }),
          zIndex: isDropoff ? 20 : 0,
        });
      }

      default:
        return new Style({}); // Empty style to avoid rendering issues
    }
  }

  render() {
    return (
      // @ts-ignore
      <layer.VectorImage zIndex={1}>
        {/* @ts-ignore */}
        <source.VectorSourceReact>
          {this.state.expandedFeatures.map((expandedFeature: ExpandedFeature, index: number) => (
            <ExpandedFeatureComponent
              expandedFeature={expandedFeature}
              index={index}
              expandedCenter={this.state.expandedCenter}
            />
          ))}

          <interaction.SelectReact
            // @ts-ignore
            filter={this.filterSelection}
            style={null}
            multi={false}
          />
          {this.props.statsDrawActive && (
            <interaction.DrawReact
              type={'Polygon'}
              onDrawend={this.showStatsForFields}
              styleOptions={{
                linestringColor: PLUM,
                pointColor: PLUM,
                polygonColor: PLUM.concat(0.3),
              }}
            />
          )}
        </source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}

const ExpandedFeatureComponent = ({
  expandedFeature,
  index,
  expandedCenter,
}: {
  expandedFeature: ExpandedFeature;
  index: number;
  expandedCenter: Coordinate;
}) => {
  const { originalFeature, expandedCoord } = expandedFeature;
  const props = originalFeature.getProperties();
  const key = `expanded-${props.job_id || index}`;

  const isDropoff = props.pullinType === 'Dropoffs';

  let text = props.pullinType;

  if (isDropoff) {
    text = `${props.pullinType}: ${props.dropoff_name}`;
  } else if (props.pullinType === 'Shift Dropoffs') {
    text = `${props.operatorName} - ${props.schedDate}`;
  } else if (props.pullinType === 'Jobs') {
    text = `${props.pullinType}: ${props.field_name}`;
  }

  const { geometry: _, ...expandedFeatureProps } = props;
  const featureId = originalFeature.getId()!.toString();

  let fillColor: string = '';

  if (isDropoff) {
    fillColor = generateColor(props.operatorName || props.pullinType);
  } else {
    fillColor = generateColor(props.robot || '');
  }

  return (
    <React.Fragment key={key}>
      {/* Expanded circle */}
      <feature.PointReact
        coordinate={expandedCoord}
        textOptions={{
          text,
          fillOptions: { color: WHITE },
          font: '20px Calibri,sans-serif',
          offsetY: 20,
          strokeOptions: { width: 2, color: BLACK },
        }}
        circleOptions={{
          strokeOptions: { color: WHITE, width: 2 },
          // @ts-ignore
          fillOptions: { color: fillColor },
          radius: 6,
        }}
        zIndex={100}
        properties={expandedFeatureProps}
        id={`${featureId}-expanded`}
      />
      {/* Connecting line from center to expanded point */}
      <feature.LineStringReact
        coordinates={[expandedCenter, expandedCoord]}
        strokeOptions={{ color: '#bd11b7', width: 2, lineDash: [4, 4] }}
      />
    </React.Fragment>
  );
};
